/** Browser display grammar: strips additions; server owns analytical validation. */
import * as z from 'zod'
import { renderEntity, renderHelp } from './insightRenderShapes'
const text = z.string().min(1).max(6000)
const datum = z
  .object({
    label: text,
    value: z.number().finite().nonnegative(),
    unit: z.enum(['customers', 'orders', 'occasions', 'days']),
  })
  .refine((v) => v.unit === 'days' || Number.isSafeInteger(v.value), 'Fractional count')
export const renderEntityList = z
  .object({
    kind: z.literal('entity-list'),
    id: text,
    title: text,
    introduction: text,
    state: z.enum(['ready', 'empty', 'unavailable']),
    message: text.nullable(),
    rows: z
      .array(
        z.object({
          id: text,
          entities: z
            .array(
              renderEntity.extend({
                role: z.enum(['Earlier', 'Next']).optional(),
              }),
            )
            .min(1)
            .max(2),
          label: text,
          summary: datum,
          note: text,
          details: z.array(datum).min(1).max(20),
        }),
      )
      .max(10),
    totalRows: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    previewRows: z.number().int().min(1).max(3),
    footer: text,
  })
  .superRefine((v, c) => {
    if (
      new Set(v.rows.map((r) => r.id)).size !== v.rows.length ||
      v.totalRows < v.rows.length ||
      (v.state !== 'ready' && v.totalRows !== 0) ||
      (v.state === 'ready') !== v.rows.length > 0 ||
      (v.state === 'ready') !== (v.message === null) ||
      v.rows.some((r) => new Set(r.entities.map((e) => e.id)).size !== r.entities.length)
    )
      c.addIssue({ code: 'custom', message: 'Entity list state or identity mismatch' })
  })
export const renderListGroup = z
  .object({
    kind: z.literal('list-group'),
    id: text,
    title: text,
    icon: z.literal('replay'),
    lists: z.array(renderEntityList).length(2),
    footer: text,
    help: renderHelp,
  })
  .refine((v) => v.lists[0]?.id !== v.lists[1]?.id, 'Duplicate list identity')
export type RenderEntityList = z.infer<typeof renderEntityList>
export type RenderListGroup = z.infer<typeof renderListGroup>
